import styled from "@emotion/styled";
import { trackEvent } from "contexts/mixpanel";
import {
  AbsoluteLink,
  AllCaps,
  Button,
  Container,
  FlexRow,
  H1,
  IconArrow,
  Link,
  ScreenReaderText,
  Stack,
  Text,
} from "atoms";
import brush1 from "assets/images/hug-brush-1.svg";
import { Thumbnail } from "features/images";
import { paths } from "utils/paths";
import { HomePageDataShape } from "../api/useHomePageData";

const trackHomeHero = (url: string) => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Home Hero CTA",
    ButtonContext: "Home Hero",
    ButtonDestination: url,
  });

type HeroBlockProps = {
  items: HomePageDataShape["heroBlock"];
};

function HomeHeroBlock({ items }: HeroBlockProps) {
  return (
    <HeroOuter>
      <HomeBrushStroke />
      <Container width="lg">
        <HeroGrid>
          <HeroCopy gap="sm">
            <H1 size="xxl" style={{ marginBottom: 0 }}>
              Discover Artists
            </H1>

            <Stack gap="xs">
              <Text size="xs">
                New artists are added to <AllCaps>Hug</AllCaps> every&nbsp;week!
              </Text>
              <Text size="xs">
                Explore emerging artists from around the world, and earn{" "}
                <AllCaps>Hug</AllCaps> Points by leaving reviews and voting in
                new&nbsp;artists.
              </Text>
            </Stack>
            <FlexRow gap=".5em 1em" itemsFlex="0 0 auto">
              <Link to="#explore" variant="secondary">
                Explore New Artists
              </Link>
              <WhiteLink
                to={paths.artistProfileBenefits()}
                variant="blank"
                size="sm"
              >
                <span>
                  Or get a <AllCaps>Hug</AllCaps> artist profile
                </span>
              </WhiteLink>
            </FlexRow>
          </HeroCopy>

          {items && (
            <>
              {items.map((item) => (
                <HeroImageItem key={`${item.cta} - ${item.url}`}>
                  <Thumbnail src={item.image} alt={item.image.altText || ""} />
                  <AbsoluteLink
                    href={item.url}
                    onClick={trackHomeHero(item.url)}
                  >
                    <ScreenReaderText>{item.cta}</ScreenReaderText>
                  </AbsoluteLink>
                  <HeroItemButton
                    variant="secondary"
                    as="div"
                    size="xs"
                    aria-hidden
                  >
                    <span>{item.cta}</span>
                    <IconArrow />
                  </HeroItemButton>
                </HeroImageItem>
              ))}
            </>
          )}
        </HeroGrid>
      </Container>
    </HeroOuter>
  );
}
const heroOverlapSm = "60px";
const heroOverlapLg = "90px";

const HeroOuter = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.black90,
  borderBottom: `${heroOverlapLg} solid ${theme.colors.bg}`,
  paddingTop: theme.spacing.gap,
  paddingBottom: 1,
  fontSize: theme.fontSizes.sm,
  textAlign: "left",
  figure: {
    flex: "1 1 450px",
  },
  position: "relative",
  [theme.breakpoints.tablet]: {
    borderBottomWidth: heroOverlapSm,
  },
  [theme.breakpoints.desktop]: {
    borderBottomWidth: heroOverlapLg,
  },
}));

const HeroGrid = styled.div(({ theme }) => ({
  display: "grid",
  gap: theme.spacing.gap,
  alignItems: "start",
  justifyContent: "start",
  marginBottom: `-${heroOverlapLg}`,
  gridTemplateColumns: "1fr",
  gridTemplateAreas: `
    "intro"
    "a"
    "b"
    "c"
  `,
  [theme.breakpoints.xs]: {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateAreas: `
      "intro intro"
      "a a"
      "b c"
    `,
  },
  [theme.breakpoints.tablet]: {
    gridTemplateColumns: "2fr 4fr 1fr 4fr",
    gridTemplateAreas: `
      "intro intro a a"
      ". b b c"
    `,
    marginBottom: `-${heroOverlapSm}`,
  },
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: "275px 4fr 3fr",
    gridTemplateRows: "auto 1fr",
    gridTemplateAreas: `
      "intro a b"
      "intro a c"
    `,
  },
  "@media (min-width: 1024px)": {
    gridTemplateColumns: "35% 4fr 3fr",
    marginBottom: `-${heroOverlapLg}`,
  },
}));

const HeroCopy = styled(Stack)(({ theme }) => ({
  gridArea: "intro",
  margin: "0 auto",
  padding: theme.spacing.gap,
  alignSelf: "center",
  "h1,h2,h3,p": {
    color: theme.colors.white,
  },
  p: {
    maxWidth: 400,
    lineHeight: 1.33,
  },
  [theme.breakpoints.tablet]: {
    paddingLeft: 0,
  },
  [theme.breakpoints.desktop]: {
    justifySelf: "start",
    marginLeft: 0,
    marginRight: "auto",
    paddingBottom: heroOverlapLg,
  },
}));

const HeroImageItem = styled.figure(({ theme }) => ({
  position: "relative",
  borderRadius: theme.borderRadius.sm,
  overflow: "hidden",
  "&:nth-of-type(1)": {
    gridArea: "a",
    aspectRatio: "48 / 58",
    [theme.breakpoints.xs]: {
      marginTop: ".5rem",
    },
    [theme.breakpoints.tablet]: {
      marginTop: 0,
    },
    [theme.breakpoints.desktop]: {
      marginTop: "1rem",
    },
  },
  "&:nth-of-type(2)": {
    gridArea: "b",
    aspectRatio: "36 / 26",
  },
  "&:nth-of-type(3)": {
    gridArea: "c",
    aspectRatio: "1 / 1",
  },
  img: {
    objectFit: "cover",
    objectPosition: "center",
  },
  a: {
    zIndex: 9,
  },
}));

const HeroItemButton = styled(Button)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "absolute",
  bottom: 8,
  left: 8,
  maxWidth: "calc(100% - 16px)",
  textAlign: "left",
  lineHeight: 1.1,
  pointerEvents: "none",
  width: "max-content",
  span: {
    flex: "1 1 80%",
  },
  svg: {
    flex: "0 0 .9em",
  },
  [theme.breakpoints.desktopLarge]: {
    bottom: "1em",
    left: "1em",
    maxWidth: "calc(100% - 2em)",
  },
}));

const HomeBrushStroke = styled.div({
  position: "absolute",
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  overflow: "hidden",
  "&+*": {
    position: "relative",
    zIndex: 2,
  },
  "&::before": {
    content: "''",
    position: "absolute",
    top: "-10vw",
    right: "-10vw",
    bottom: "-10vw",
    left: "-10vw",
    zIndex: 0,
    pointerEvents: "none",
    backgroundSize: "cover",
    backgroundPosition: "center",
    opacity: 0.175,
    backgroundImage: `url("${brush1}")`,
  },
});

const WhiteLink = styled(Link)({
  "&,&:hover,&:focus-visible": { color: "white" },
  "&:hover": { borderBottomColor: "currentColor" },
});
export { HomeHeroBlock };
